import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

export default function RoomLobby({ onEnterRoom }) {
  const [myRooms, setMyRooms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [createdRoom, setCreatedRoom] = useState(null);

  const token = localStorage.getItem('session');

  // Load existing rooms on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    try {
      const part = token.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      // Fetch user's rooms from login response (re-login to get fresh data)
      fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: payload.email, password: '__refresh__' })
      }).catch(() => {});
    } catch {}

    // Fetch rooms by checking each room_id stored in user record
    // For now, try to load rooms from the JWT or a dedicated endpoint
    // Simple approach: scan rooms the user owns/joined via room Lambda
    const fetchRooms = async () => {
      try {
        const res = await fetch(`${API_BASE}/room/my-rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyRooms(data.rooms || []);
        }
      } catch {}
      setLoading(false);
    };
    fetchRooms();
  }, [token]);

  const handleCreate = async () => {
    if (!roomName.trim()) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/room/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ room_name: roomName }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setLoading(false); return; }
      setCreatedRoom(data);
      setShowCreate(false);
    } catch { setError('Network error.'); }
    setLoading(false);
  };

  const handleJoin = async () => {
    if (!inviteCode.trim()) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/room/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invite_code: inviteCode }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setLoading(false); return; }
      onEnterRoom(data.room_id, data.role || 'player', true);
    } catch { setError('Network error.'); }
    setLoading(false);
  };

  return (
    <div className="room-lobby">
      <div className="room-lobby-card">
        <h1 className="room-lobby-title">🏰 Your Rooms</h1>
        <p className="room-lobby-sub">Create a room to DM, or join one with an invite code.</p>

        {/* Existing rooms */}
        {loading ? (
          <p style={{color:'var(--text-dim)',fontSize:'0.75rem'}}>Loading rooms...</p>
        ) : myRooms.length > 0 && (
          <div className="room-list">
            {myRooms.map(r => (
              <div key={r.room_id} className="room-list-item" onClick={() => onEnterRoom(r.room_id, r.role, false)}>
                <span className="room-list-name">{r.room_name}</span>
                <span className="room-list-role">{r.role === 'dm' ? '👑 DM' : '⚔️ Player'}</span>
              </div>
            ))}
          </div>
        )}

        <div className="room-lobby-actions">
          <button className="room-btn-create" onClick={() => { setShowCreate(true); setShowJoin(false); }}>+ Create Room</button>
          <button className="room-btn-join" onClick={() => { setShowJoin(true); setShowCreate(false); }}>🔗 Join Room</button>
        </div>

        {showCreate && (
          <div className="room-form">
            <input className="login-input" placeholder="Room name (e.g. Friday Night Campaign)" value={roomName} onChange={e => setRoomName(e.target.value)} maxLength={50} />
            <button className="login-submit" onClick={handleCreate} disabled={loading}>{loading ? '...' : 'Create'}</button>
          </div>
        )}

        {showJoin && (
          <div className="room-form">
            <input className="login-input" placeholder="Invite code (e.g. ABC123)" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} maxLength={6} />
            <button className="login-submit" onClick={handleJoin} disabled={loading}>{loading ? '...' : 'Join'}</button>
          </div>
        )}

        {error && <p className="login-error">{error}</p>}

        {createdRoom && (
          <div className="room-created">
            <h3>✅ Room Created!</h3>
            <p className="room-created-name">{createdRoom.room_name}</p>
            <div className="room-invite-box">
              <span className="room-invite-label">Invite Code:</span>
              <span className="room-invite-code">{createdRoom.invite_code}</span>
            </div>
            <p className="room-invite-link">Share: <strong>{createdRoom.invite_link}</strong></p>
            <button className="login-submit" style={{marginTop:'16px'}} onClick={() => { localStorage.setItem('active_room', createdRoom.room_id); window.location.href = '/'; }}>🏰 Enter Room</button>
          </div>
        )}
      </div>
    </div>
  );
}
